// <ManageRequestsTab> — T-145 (Step 7) + T-146 (Step 8).
//
// The Manage Requests body that sits inside <TimeView> when the `?tab=`
// URL param is `'manage'`. Fetches `GET /api/timeoff/requests` and
// renders:
//   - an internal toolbar (editable filter pills + date-range pickers)
//   - the approval inbox table with per-row checkboxes + bulk action bar
//   - a pagination footer
//   - an empty state when no rows match the current filters
//   - a drill-in side modal opened on row click
//   - a toast surfacing the bulk-action result
//
// URL state owned here (additive on top of `useTimeViewUrlState`):
//   - `?status=Pending,Approved`  comma-separated TimeOffStatus list
//   - `?from=YYYY-MM-DD`          submittedAt lower bound
//   - `?to=YYYY-MM-DD`            submittedAt upper bound
//   - `?page=N`                   1-indexed page number; default 1
//
// All four params live directly in `useSearchParams` (not the shared
// timeViewUrlState helper) so this component stays self-contained and
// doesn't touch the file Agent S (T-144 Calendar) is editing.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Inbox, Plus, X } from 'lucide-react';
import type {
  BulkActionInput,
  BulkActionResponse,
  BulkSkipReason,
  ManageRequestsResponse,
  RequestRollupRow,
  TimeOffStatus,
} from '@pa/shared/time-view';
import { api } from '../../api/client';
import { useTimeViewUrlState } from './timeViewUrlState';

const PAGE_SIZE = 50;

const ALL_STATUSES: ReadonlyArray<TimeOffStatus> = [
  'Pending',
  'Approved',
  'Rejected',
  'Cancelled',
];
const ALL_STATUS_SET: ReadonlySet<TimeOffStatus> = new Set(ALL_STATUSES);

interface UrlBits {
  statuses: TimeOffStatus[]; // empty array = "All"
  from: string;
  to: string;
  page: number;
}

function parseStatusCsv(raw: string | null): TimeOffStatus[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is TimeOffStatus =>
      ALL_STATUS_SET.has(s as TimeOffStatus),
    );
}

function buildQueryString(
  url: UrlBits,
  global: { site: string; team: string },
): string {
  const params = new URLSearchParams();
  if (url.statuses.length > 0) params.set('status', url.statuses.join(','));
  if (global.site && global.site !== 'all') params.set('siteId', global.site);
  if (global.team && global.team !== 'all') params.set('teamId', global.team);
  if (url.from) params.set('from', url.from);
  if (url.to) params.set('to', url.to);
  if (url.page !== 1) params.set('page', String(url.page));
  params.set('pageSize', String(PAGE_SIZE));
  const q = params.toString();
  return q ? `?${q}` : '';
}

interface ToastState {
  processed: BulkActionResponse['processed'];
  skipped: BulkActionResponse['skipped'];
  action: 'approve' | 'reject';
}

export default function ManageRequestsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: globalState } = useTimeViewUrlState();
  const queryClient = useQueryClient();

  const urlBits = useMemo<UrlBits>(
    () => ({
      statuses: parseStatusCsv(searchParams.get('status')),
      from: searchParams.get('from') ?? '',
      to: searchParams.get('to') ?? '',
      page: Math.max(1, Number(searchParams.get('page') ?? '1') || 1),
    }),
    [searchParams],
  );

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value == null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        // Reset page on filter changes (but not when changing page itself).
        if (key !== 'page') next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (next: number) => {
      const clamped = Math.max(1, next);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (clamped === 1) params.delete('page');
        else params.set('page', String(clamped));
        return params;
      });
    },
    [setSearchParams],
  );

  const queryString = buildQueryString(urlBits, {
    site: globalState.site,
    team: globalState.team,
  });

  const query = useQuery<ManageRequestsResponse>({
    queryKey: [
      'timeView',
      'requests',
      {
        status: urlBits.statuses.join(',') || 'all',
        site: globalState.site,
        team: globalState.team,
        from: urlBits.from,
        to: urlBits.to,
        page: urlBits.page,
        pageSize: PAGE_SIZE,
      },
    ],
    queryFn: () =>
      api.get<ManageRequestsResponse>(`/api/timeoff/requests${queryString}`),
    placeholderData: keepPreviousData,
  });

  const isLoading = query.isPending;
  const data = query.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const policyBucketEntries = data
    ? Object.entries(data.policyBuckets).filter(([, v]) => v.total > 0)
    : [];

  const startIndex = total === 0 ? 0 : (urlBits.page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(urlBits.page * PAGE_SIZE, total);
  const hasNext = urlBits.page * PAGE_SIZE < total;
  const hasPrev = urlBits.page > 1;

  // ── Selection state ──────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // When the visible row set changes (filter/page change), prune the
  // selection so the bar reflects only currently-visible rows.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((r) => r.requestId));
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelected((prev) => {
      const visibleIds = rows.map((r) => r.requestId);
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  }, [rows]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  // ── Bulk action mutation ─────────────────────────────────────────
  const [toast, setToast] = useState<ToastState | null>(null);
  const bulkMutation = useMutation<
    BulkActionResponse,
    Error,
    { action: 'approve' | 'reject'; comment?: string }
  >({
    mutationFn: async ({ action, comment }) => {
      const body: BulkActionInput = {
        action,
        requestIds: Array.from(selected),
        ...(comment ? { comment } : {}),
      };
      return api.post<BulkActionResponse>(
        '/api/timeoff/requests/bulk',
        body,
      );
    },
    onSuccess: (res, vars) => {
      setToast({
        processed: res.processed,
        skipped: res.skipped,
        action: vars.action,
      });
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ['timeView', 'requests'] });
      queryClient.invalidateQueries({ queryKey: ['timeView', 'calendar'] });
    },
  });

  // ── Per-row (drill-in modal) approve / reject ───────────────────
  const [drillInRow, setDrillInRow] = useState<RequestRollupRow | null>(null);
  const singleMutation = useMutation<
    unknown,
    Error,
    { id: string; action: 'approve' | 'reject'; reason?: string }
  >({
    mutationFn: async ({ id, action, reason }) =>
      api.post(
        `/api/timeoff/${id}/${action === 'approve' ? 'approve' : 'reject'}`,
        reason ? { reason } : undefined,
      ),
    onSuccess: () => {
      setDrillInRow(null);
      queryClient.invalidateQueries({ queryKey: ['timeView', 'requests'] });
      queryClient.invalidateQueries({ queryKey: ['timeView', 'calendar'] });
    },
  });

  // ── Confirm dialog state for the bulk bar ───────────────────────
  const [confirmAction, setConfirmAction] = useState<
    'approve' | 'reject' | null
  >(null);

  return (
    <div className="flex flex-col" data-testid="manage-requests-tab">
      {/* Internal toolbar: editable filter pills + date-range pickers */}
      <FilterPillRow
        urlBits={urlBits}
        updateParam={updateParam}
        total={total}
      />

      {/* Bulk action bar — sticky above the table when ≥1 row selected */}
      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          onApprove={() => setConfirmAction('approve')}
          onReject={() => setConfirmAction('reject')}
          onClear={clearSelection}
          busy={bulkMutation.isPending}
        />
      )}

      {/* Policy-bucket summary row (kept lightweight). */}
      {policyBucketEntries.length > 0 && (
        <div
          className="px-8 py-2 border-b border-slate-100 flex flex-wrap items-center gap-2"
          data-testid="manage-requests-policy-buckets"
        >
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mr-1">
            Approved
          </span>
          {policyBucketEntries.map(([type, bucket]) => (
            <span
              key={type}
              data-testid={`manage-requests-policy-${type.replace(/\s+/g, '-')}`}
              className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full px-3 py-1"
            >
              {type}
              <span className="text-slate-400">{bucket.total}</span>
            </span>
          ))}
        </div>
      )}

      {/* Body — table, skeleton, or empty state */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <LoadingSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          <RequestsTable
            rows={rows}
            selected={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAllVisible}
            onRowClick={(row) => setDrillInRow(row)}
          />
        )}
      </div>

      {/* Pagination footer (hidden in empty / loading states) */}
      {!isLoading && total > 0 && (
        <div
          className="px-8 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500"
          data-testid="manage-requests-pagination"
        >
          <span data-testid="manage-requests-page-summary">
            {startIndex}–{endIndex} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(urlBits.page - 1)}
              disabled={!hasPrev}
              data-testid="manage-requests-page-prev"
              className="px-3 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage(urlBits.page + 1)}
              disabled={!hasNext}
              data-testid="manage-requests-page-next"
              className="px-3 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Drill-in modal — opens on row click */}
      {drillInRow && (
        <DrillInModal
          row={drillInRow}
          onClose={() => setDrillInRow(null)}
          onApprove={(reason) =>
            singleMutation.mutate({
              id: drillInRow.requestId,
              action: 'approve',
              reason,
            })
          }
          onReject={(reason) =>
            singleMutation.mutate({
              id: drillInRow.requestId,
              action: 'reject',
              reason,
            })
          }
          busy={singleMutation.isPending}
        />
      )}

      {/* Confirm dialog for bulk actions */}
      {confirmAction && (
        <BulkConfirmDialog
          action={confirmAction}
          count={selected.size}
          onCancel={() => setConfirmAction(null)}
          onConfirm={(comment) => {
            bulkMutation.mutate({ action: confirmAction, comment });
            setConfirmAction(null);
          }}
          busy={bulkMutation.isPending}
        />
      )}

      {/* Result toast */}
      {toast && (
        <ResultToast toast={toast} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

// ── Filter pills row ──────────────────────────────────────────────

interface FilterPillRowProps {
  urlBits: UrlBits;
  updateParam: (key: string, value: string | null) => void;
  total: number;
}

function FilterPillRow({ urlBits, updateParam, total }: FilterPillRowProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close popovers on outside click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setAddOpen(false);
        setEditingStatus(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const hasStatusPill = urlBits.statuses.length > 0;
  const hasFromPill = !!urlBits.from;
  const hasToPill = !!urlBits.to;

  return (
    <div
      ref={containerRef}
      className="px-8 py-3 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3"
      data-testid="manage-requests-toolbar"
    >
      <div
        className="flex items-center gap-2 flex-wrap"
        data-testid="manage-requests-filter-pills"
      >
        {/* Status pill — chip per active status */}
        {hasStatusPill && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setEditingStatus((v) => !v)}
              data-testid="manage-requests-pill-status"
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-full pl-3 pr-1 py-1"
            >
              <span>Status: {urlBits.statuses.join(', ')}</span>
              <span
                role="button"
                aria-label="Remove status filter"
                data-testid="manage-requests-pill-status-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  updateParam('status', null);
                  setEditingStatus(false);
                }}
                className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-slate-300/60 text-slate-500"
              >
                <X size={11} />
              </span>
            </button>
            {editingStatus && (
              <div
                data-testid="manage-requests-pill-status-popover"
                className="absolute z-10 top-full mt-1 left-0 bg-white border border-slate-200 rounded-lg shadow-md p-2 min-w-[140px]"
              >
                {ALL_STATUSES.map((s) => {
                  const active = urlBits.statuses.includes(s);
                  return (
                    <label
                      key={s}
                      className="flex items-center gap-2 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        data-testid={`manage-requests-pill-status-toggle-${s}`}
                        onChange={() => {
                          const next = active
                            ? urlBits.statuses.filter((x) => x !== s)
                            : [...urlBits.statuses, s];
                          updateParam(
                            'status',
                            next.length ? next.join(',') : null,
                          );
                        }}
                      />
                      <span>{s}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* From/to pills are surfaced when present so removal is obvious */}
        {hasFromPill && (
          <DateRangePill
            label="From"
            iso={urlBits.from}
            onRemove={() => updateParam('from', null)}
            testId="manage-requests-pill-from"
          />
        )}
        {hasToPill && (
          <DateRangePill
            label="To"
            iso={urlBits.to}
            onRemove={() => updateParam('to', null)}
            testId="manage-requests-pill-to"
          />
        )}

        {/* + Add filter */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setAddOpen((v) => !v)}
            data-testid="manage-requests-add-filter"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-slate-500 hover:bg-slate-100 border border-dashed border-slate-300"
          >
            <Plus size={12} /> Add filter
          </button>
          {addOpen && (
            <div
              data-testid="manage-requests-add-filter-menu"
              className="absolute z-10 top-full mt-1 left-0 bg-white border border-slate-200 rounded-lg shadow-md p-1 min-w-[180px] text-xs"
            >
              {!hasStatusPill && (
                <button
                  type="button"
                  data-testid="manage-requests-add-filter-status"
                  className="w-full text-left px-3 py-1.5 text-slate-700 hover:bg-slate-50 rounded"
                  onClick={() => {
                    updateParam('status', 'Pending');
                    setAddOpen(false);
                    setEditingStatus(true);
                  }}
                >
                  Status
                </button>
              )}
              <FilterDateField
                label="From"
                value={urlBits.from}
                onChange={(v) => {
                  updateParam('from', v || null);
                  if (v) setAddOpen(false);
                }}
                testId="manage-requests-add-filter-from"
              />
              <FilterDateField
                label="To"
                value={urlBits.to}
                onChange={(v) => {
                  updateParam('to', v || null);
                  if (v) setAddOpen(false);
                }}
                testId="manage-requests-add-filter-to"
              />
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-slate-400 font-medium">
        <span data-testid="manage-requests-total">Total: {total}</span>
      </div>
    </div>
  );
}

function DateRangePill({
  label,
  iso,
  onRemove,
  testId,
}: {
  label: string;
  iso: string;
  onRemove: () => void;
  testId: string;
}) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-full pl-3 pr-1 py-1"
    >
      <span>
        {label}: {formatIsoDate(iso)}
      </span>
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        data-testid={`${testId}-remove`}
        onClick={onRemove}
        className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-slate-300/60 text-slate-500"
      >
        <X size={11} />
      </button>
    </span>
  );
}

function FilterDateField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId: string;
}) {
  return (
    <label className="flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-slate-50 rounded">
      <span className="text-slate-700">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-white border border-slate-200 rounded px-1.5 py-0.5 text-[11px] text-slate-700"
      />
    </label>
  );
}

// ── Bulk action bar ────────────────────────────────────────────────

interface BulkActionBarProps {
  count: number;
  onApprove: () => void;
  onReject: () => void;
  onClear: () => void;
  busy: boolean;
}

function BulkActionBar({
  count,
  onApprove,
  onReject,
  onClear,
  busy,
}: BulkActionBarProps) {
  return (
    <div
      data-testid="manage-requests-bulk-bar"
      className="sticky top-0 z-10 bg-[#A6264C]/5 border-b border-[#A6264C]/15 shadow-sm px-8 py-3 flex items-center justify-between"
    >
      <div className="flex items-center gap-4">
        <span
          className="text-sm font-semibold text-slate-700"
          data-testid="manage-requests-bulk-count"
        >
          {count} selected
        </span>
        <button
          type="button"
          onClick={onClear}
          data-testid="manage-requests-bulk-clear"
          className="text-xs text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
        >
          Clear selection
        </button>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          data-testid="manage-requests-bulk-approve"
          className="px-3 py-1.5 rounded-lg bg-[#6344C6] text-white text-xs font-bold hover:bg-[#5337b0] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={onReject}
          disabled={busy}
          data-testid="manage-requests-bulk-reject"
          className="px-3 py-1.5 rounded-lg bg-white border border-rose-300 text-rose-600 text-xs font-bold hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Bulk confirm dialog ────────────────────────────────────────────

interface BulkConfirmDialogProps {
  action: 'approve' | 'reject';
  count: number;
  onCancel: () => void;
  onConfirm: (comment?: string) => void;
  busy: boolean;
}

function BulkConfirmDialog({
  action,
  count,
  onCancel,
  onConfirm,
  busy,
}: BulkConfirmDialogProps) {
  const [comment, setComment] = useState('');
  const isReject = action === 'reject';
  return (
    <div
      data-testid="manage-requests-bulk-confirm"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl p-6 w-[420px] max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-800 mb-2">
          {isReject ? 'Reject' : 'Approve'} {count} request{count > 1 ? 's' : ''}?
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          {isReject
            ? 'The requester will be notified. Add an optional comment that will be visible to them.'
            : 'The requesters will be notified that their time off has been approved.'}
        </p>
        {isReject && (
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Reason (optional, visible to requester)"
            maxLength={500}
            rows={3}
            data-testid="manage-requests-bulk-confirm-comment"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 mb-4 focus:outline-none focus:ring-2 focus:ring-[#6344C6]/20"
          />
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="manage-requests-bulk-confirm-cancel"
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              onConfirm(
                isReject && comment.trim() ? comment.trim() : undefined,
              )
            }
            data-testid="manage-requests-bulk-confirm-ok"
            className={
              'px-3 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-50 ' +
              (isReject
                ? 'bg-rose-500 hover:bg-rose-600'
                : 'bg-[#6344C6] hover:bg-[#5337b0]')
            }
          >
            {isReject ? 'Reject all' : 'Approve all'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Result toast ──────────────────────────────────────────────────

const SKIP_REASON_LABEL: Record<BulkSkipReason, string> = {
  not_found: 'not found',
  forbidden: 'not allowed',
  already_terminal: 'already approved or rejected',
};

function ResultToast({
  toast,
  onDismiss,
}: {
  toast: ToastState;
  onDismiss: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const verb = toast.action === 'approve' ? 'approved' : 'rejected';
  return (
    <div
      data-testid="manage-requests-toast"
      className="fixed bottom-6 right-6 z-40 bg-slate-900 text-white text-sm rounded-lg shadow-lg px-4 py-3 max-w-[420px]"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div data-testid="manage-requests-toast-summary">
            {toast.processed.length} {verb}, {toast.skipped.length} skipped
          </div>
          {toast.skipped.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="manage-requests-toast-details-toggle"
              className="text-[11px] text-slate-300 underline mt-1"
            >
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}
          {expanded && toast.skipped.length > 0 && (
            <ul
              data-testid="manage-requests-toast-details"
              className="mt-2 space-y-1 text-[11px] text-slate-200"
            >
              {toast.skipped.map((s) => (
                <li key={s.requestId}>
                  {s.requestId.slice(0, 8)}… —{' '}
                  {SKIP_REASON_LABEL[s.reason]}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="manage-requests-toast-dismiss"
          className="text-slate-300 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Drill-in modal (per-row detail) ───────────────────────────────

interface DrillInModalProps {
  row: RequestRollupRow;
  onClose: () => void;
  onApprove: (reason?: string) => void;
  onReject: (reason?: string) => void;
  busy: boolean;
}

function DrillInModal({
  row,
  onClose,
  onApprove,
  onReject,
  busy,
}: DrillInModalProps) {
  const [showRejectEditor, setShowRejectEditor] = useState(false);
  const [reason, setReason] = useState('');
  // `mounted` flips on the first paint so the backdrop fades in and the
  // side panel slides in from the right. Plain CSS transitions, no
  // animation library.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const terminal = row.status === 'Approved' || row.status === 'Rejected';
  return (
    <div
      data-testid="manage-requests-drillin"
      className={
        'fixed inset-0 z-40 flex items-stretch justify-end transition-opacity duration-200 ' +
        (mounted ? 'bg-slate-900/30 opacity-100' : 'bg-slate-900/0 opacity-0')
      }
      onClick={onClose}
    >
      <div
        data-testid="manage-requests-drillin-panel"
        onClick={(e) => e.stopPropagation()}
        className={
          'bg-white w-[420px] max-w-[95vw] h-full shadow-xl flex flex-col transform transition-transform duration-200 ease-out ' +
          (mounted ? 'translate-x-0' : 'translate-x-full')
        }
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="w-9 h-9 rounded-full bg-slate-200 inline-flex items-center justify-center text-xs font-bold text-slate-500"
            >
              {initials(row.employeeName)}
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-800">
                {row.employeeName ?? row.employeeId}
              </div>
              <div className="text-[11px] text-slate-400">
                Request {row.requestId.slice(0, 8)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="manage-requests-drillin-close"
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4 text-sm">
          <DetailRow label="Type" value={row.type} />
          <DetailRow
            label="Dates"
            value={formatDateRange(row.startDate, row.endDate)}
          />
          <DetailRow label="Days" value={String(row.daysCount)} />
          <DetailRow label="Status" value={row.status} />
          <DetailRow
            label="Submitted"
            value={formatTimestamp(row.submittedAt)}
          />

          {/* Approval history — placeholder until a per-request events
              endpoint is wired. Rendering the structure so the layout
              is stable; events will populate when GET /requests/:id
              lands. */}
          <div
            data-testid="manage-requests-drillin-history"
            className="mt-2 pt-3 border-t border-slate-100"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
              Approval history
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li>
                Submitted • {formatTimestamp(row.submittedAt)}
              </li>
              {row.status === 'Approved' && <li>Approved</li>}
              {row.status === 'Rejected' && <li>Rejected</li>}
              {row.status === 'Cancelled' && <li>Cancelled</li>}
            </ul>
          </div>
        </div>

        {!terminal && (
          <div className="px-6 py-4 border-t border-slate-100">
            {showRejectEditor ? (
              <div className="space-y-2">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  maxLength={500}
                  rows={3}
                  data-testid="manage-requests-drillin-reject-reason"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRejectEditor(false);
                      setReason('');
                    }}
                    disabled={busy}
                    data-testid="manage-requests-drillin-reject-cancel"
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => onReject(reason.trim() || undefined)}
                    disabled={busy}
                    data-testid="manage-requests-drillin-reject-confirm"
                    className="px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-bold hover:bg-rose-600 disabled:opacity-50"
                  >
                    Confirm reject
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowRejectEditor(true)}
                  disabled={busy}
                  data-testid="manage-requests-drillin-reject"
                  className="px-3 py-1.5 rounded-lg bg-white border border-rose-300 text-rose-600 text-xs font-bold hover:bg-rose-50 disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => onApprove()}
                  disabled={busy}
                  data-testid="manage-requests-drillin-approve"
                  className="px-3 py-1.5 rounded-lg bg-[#6344C6] text-white text-xs font-bold hover:bg-[#5337b0] disabled:opacity-50"
                >
                  Approve
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
        {label}
      </span>
      <span className="text-sm text-slate-700">{value}</span>
    </div>
  );
}

// ── pieces ─────────────────────────────────────────────────────────

interface RequestsTableProps {
  rows: RequestRollupRow[];
  selected: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  onRowClick: (row: RequestRollupRow) => void;
}

function RequestsTable({
  rows,
  selected,
  onToggleRow,
  onToggleAll,
  onRowClick,
}: RequestsTableProps) {
  const allSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.requestId));
  return (
    <table
      className="min-w-max w-full border-collapse"
      data-testid="manage-requests-table"
    >
      <thead>
        <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          <th className="text-left px-8 py-2 w-10">
            <input
              type="checkbox"
              checked={allSelected}
              aria-label="Select all on this page"
              data-testid="manage-requests-select-all"
              onChange={onToggleAll}
            />
          </th>
          <th className="text-left px-4 py-2">Employee</th>
          <th className="text-left px-4 py-2">Type</th>
          <th className="text-left px-4 py-2">Date range</th>
          <th className="text-left px-4 py-2">Days</th>
          <th className="text-left px-4 py-2">Submitted</th>
          <th className="text-left px-4 py-2">Status</th>
          <th className="text-right px-8 py-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isSelected = selected.has(row.requestId);
          return (
            <tr
              key={row.requestId}
              onClick={(e) => {
                // Don't open the drill-in when the click was inside the
                // checkbox cell — that interaction is selection-only.
                const target = e.target as HTMLElement;
                if (target.closest('[data-row-checkbox]')) return;
                onRowClick(row);
              }}
              data-testid={`manage-requests-row-${row.requestId}`}
              className={
                'border-b border-slate-100 hover:bg-slate-50/60 group cursor-pointer ' +
                (isSelected ? 'bg-[#6344C6]/5' : '')
              }
            >
              <td className="px-8 py-2.5" data-row-checkbox>
                <input
                  type="checkbox"
                  checked={isSelected}
                  aria-label={`Select request ${row.requestId}`}
                  data-testid={`manage-requests-row-checkbox-${row.requestId}`}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onToggleRow(row.requestId)}
                />
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                  <span
                    aria-hidden
                    className="w-7 h-7 rounded-full bg-slate-200 inline-flex items-center justify-center text-[10px] font-bold text-slate-500"
                  >
                    {initials(row.employeeName)}
                  </span>
                  <span className="text-sm font-semibold text-slate-700">
                    {row.employeeName ?? row.employeeId}
                  </span>
                </div>
              </td>
              <td className="px-4 py-2.5">
                <TypePill type={row.type} />
              </td>
              <td className="px-4 py-2.5 text-xs text-slate-600">
                {formatDateRange(row.startDate, row.endDate)}
              </td>
              <td className="px-4 py-2.5 text-xs text-slate-600">
                {row.daysCount}
              </td>
              <td className="px-4 py-2.5 text-xs text-slate-500">
                {formatTimestamp(row.submittedAt)}
              </td>
              <td className="px-4 py-2.5">
                <StatusPill status={row.status} />
              </td>
              <td className="px-8 py-2.5 text-right text-[10px] text-slate-400">
                <span className="opacity-0 group-hover:opacity-100">
                  View
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StatusPill({ status }: { status: TimeOffStatus }) {
  const cfg: Record<
    TimeOffStatus,
    { dot: string; text: string; bg: string; label: string }
  > = {
    Pending: {
      dot: 'bg-amber-400',
      text: 'text-amber-700',
      bg: 'bg-amber-50',
      label: 'Pending',
    },
    Approved: {
      dot: 'bg-emerald-500',
      text: 'text-emerald-700',
      bg: 'bg-emerald-50',
      label: 'Approved',
    },
    Rejected: {
      dot: 'bg-rose-500',
      text: 'text-rose-700',
      bg: 'bg-rose-50',
      label: 'Rejected',
    },
    Cancelled: {
      dot: 'bg-slate-400',
      text: 'text-slate-600',
      bg: 'bg-slate-100',
      label: 'Cancelled',
    },
  };
  const c = cfg[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.bg} ${c.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function TypePill({ type }: { type: RequestRollupRow['type'] }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
      {type}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="px-8 py-6 space-y-3" data-testid="manage-requests-skeleton">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-10 bg-slate-100 rounded-lg animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center py-24 text-center"
      data-testid="manage-requests-empty"
    >
      <Inbox size={48} className="text-slate-300 mb-4" />
      <p className="text-base font-semibold text-slate-600">
        No pending requests
      </p>
      <p className="text-sm text-slate-400 mt-1">
        All caught up — new requests will land here.
      </p>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

function formatDateRange(start: string, end: string): string {
  if (start === end) return formatIsoDate(start);
  return `${formatIsoDate(start)} – ${formatIsoDate(end)}`;
}

function formatIsoDate(iso: string): string {
  // iso = YYYY-MM-DD → DD-MM-YYYY per spec §7.3
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

function formatTimestamp(iso: string): string {
  // iso submittedAt → DD-MM-YYYY HH:MM per spec §7.3 (shorter than spec's
  // HH:MM:SS — second precision is noise in the inbox view).
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mi = String(date.getUTCMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
}
